'use client'
import { useEffect, useRef, useState, type FormEvent } from 'react'
import { ArrowRight, ArrowLeft, LoaderCircle } from 'lucide-react'
import { BRANDING } from '@/lib/branding'
import styles from './partner-auth.module.css'
import { useFormReady } from './useFormReady'

export default function PartnerLoginForm({ prefix }: { prefix: string }) {
  const ready = useFormReady()
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [step, setStep] = useState<'email' | 'code'>('email')
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState('')
  const [loading, setLoading] = useState(false)
  const [deadline, setDeadline] = useState(0)
  const [now, setNow] = useState(0)
  const busy = useRef(false)
  const errorRef = useRef<HTMLDivElement>(null)
  const codeRef = useRef<HTMLInputElement>(null)
  const emailRef = useRef<HTMLInputElement>(null)
  const remaining = Math.max(0, Math.ceil((deadline - now) / 1000))
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('access') === 'unavailable') setError('Workspace access could not be verified. Try again or contact support.')
  }, [])
  useEffect(() => { if (error) errorRef.current?.focus() }, [error])
  useEffect(() => { if (step === 'code') codeRef.current?.focus() }, [step])
  useEffect(() => {
    if (!deadline) return
    const interval = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(interval)
  }, [deadline])

  async function submit(kind: 'request' | 'verify') {
    if (busy.current) return
    busy.current = true; setLoading(true); setError(null); setNotice('')
    let navigating = false
    try {
      const response = await fetch(`/api/partner/login/${kind}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), ...(kind === 'verify' ? { code } : {}) }), signal: AbortSignal.timeout(25_000),
      })
      const data = await response.json()
      if (kind === 'request' && Number.isFinite(data.retryAfter)) {
        setNow(Date.now()); setDeadline(Date.now() + Math.min(3600, Math.max(0, data.retryAfter)) * 1000)
      }
      if (!response.ok || data.ok !== true) {
        if (kind === 'request' && response.status === 403) { setStep('email'); setCode(''); setDeadline(0) }
        setError(data.error || 'Sign-in is unavailable. Please try again.'); return
      }
      if (kind === 'request') {
        setStep('code'); setCode(''); setNotice('A sign-in code has been sent to your email.'); codeRef.current?.focus()
      } else {
        navigating = true
        window.location.assign(prefix || '/')
      }
    } catch { setError('We could not complete your request. Please try again or contact support.') }
    finally { if (!navigating) { busy.current = false; setLoading(false) } }
  }
  function onSubmit(event: FormEvent) { event.preventDefault(); void submit(step === 'email' ? 'request' : 'verify') }
  return <>
    <h1>{step === 'email' ? 'Sign in' : 'Check your inbox'}</h1>
    <p className={styles.subtitle}>{step === 'email' ? 'Enter your work email. We’ll send you a one-time code to sign in.' : <>Enter the six-digit code for <strong className={styles.wrap}>{email.trim().toLowerCase()}</strong>.</>}</p>
    <form onSubmit={onSubmit} className={styles.form} aria-busy={loading}>
      <fieldset disabled={!ready || loading}>
        {step === 'email' ? <div className={styles.field}>
          <label htmlFor="email">Work email</label><input ref={emailRef} id="email" type="email" autoComplete="email" autoCapitalize="none" spellCheck={false} required maxLength={254} value={email} onChange={e => setEmail(e.target.value)} placeholder="you@agency.com" />
        </div> : <div className={styles.field}>
          <label htmlFor="code">Verification code</label><input ref={codeRef} className={styles.code} id="code" type="text" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" required maxLength={6} value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" aria-describedby="code-help" />
          <p id="code-help" className={styles.hint}>Use the latest code. It expires after 10 minutes.</p>
        </div>}
        {error && <div className={styles.error} role="alert" ref={errorRef} tabIndex={-1}>{error}</div>}
        <button type="submit" disabled={loading || (step === 'email' && remaining > 0)} className={styles.submit}>
          {loading ? <><LoaderCircle size={18} className={styles.spinner} aria-hidden="true" />{step === 'email' ? 'Sending code…' : 'Please wait…'}</> : <>{step === 'email' ? (remaining ? `Try again in ${remaining}s` : 'Send sign-in code') : 'Verify and sign in'}<ArrowRight size={18} aria-hidden="true" /></>}
        </button>
        {step === 'code' && <div className={styles.codeActions}>
          <button type="button" className={styles.secondary} onClick={() => { setStep('email'); setCode(''); setDeadline(0); setError(null); setNotice(''); setTimeout(() => emailRef.current?.focus(), 0) }}><ArrowLeft size={15} aria-hidden="true" />Change email</button>
          <button type="button" className={styles.secondary} disabled={remaining > 0} onClick={() => void submit('request')}>{remaining ? `Resend in ${remaining}s` : 'Resend code'}</button>
        </div>}
      </fieldset>
      <p role="status" className={styles.hint}>{loading ? 'Processing your request…' : notice}</p>
    </form>
    <p className={styles.switch}>New to ChatSyncs Partners? <a href={`${prefix}/signup`}>Create an account</a></p>
    <p className={styles.help}>Having trouble? <a href={`mailto:${BRANDING.supportEmail}`}>Contact support</a></p>
  </>
}
